import { useCallback, useEffect, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { ConfirmDialog } from '../ui/confirm-dialog.jsx';
import { useToast } from '../ui/toast.jsx';
import { OutcomeBadge } from '../ui/badges.jsx';
import { EmptyState } from '../ui/empty-state.jsx';
import { formatUtcInTimezone } from '../../lib/time.js';
import { api, ApiError } from '../../api/index.js';

const PAGE_SIZE = 50;

const EVENT_ICONS = {
  'auth.login':              'fa-right-to-bracket',
  'auth.logout':             'fa-arrow-right-from-bracket',
  'user.create':             'fa-user-plus',
  'user.update':             'fa-user-pen',
  'user.update.self':        'fa-user-pen',
  'user.delete':             'fa-user-minus',
  'user.password.reset':     'fa-key',
  'user.password.change.self': 'fa-key',
  'user.bootstrap':          'fa-user-shield',
  'settings.update':         'fa-sliders',
  'logs.clear':              'fa-trash-can',
  'audit.clear':             'fa-trash-can',
};

function eventIcon(eventType) {
  return EVENT_ICONS[eventType] || 'fa-circle-dot';
}

export function AuditPage({ currentUser }) {
  const [filters, setFilters] = useState({
    eventType: '',
    actor: '',
    outcome: '',
    since: '',
    until: '',
    q: '',
  });
  const [eventTypes, setEventTypes] = useState([]);
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const toast = useToast();

  useEffect(() => {
    api.audit.eventTypes().then((r) => setEventTypes(r.eventTypes || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        eventType: filters.eventType,
        actor: filters.actor,
        outcome: filters.outcome,
        since: filters.since ? toIsoUtc(filters.since) : '',
        until: filters.until ? toIsoUtc(filters.until) : '',
        q: filters.q,
      };
      const res = await api.audit.list(params);
      setData(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    load();
  }, [load]);

  function applyFilter(patch) {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(0);
  }

  function resetFilters() {
    setFilters({ eventType: '', actor: '', outcome: '', since: '', until: '', q: '' });
    setPage(0);
  }

  async function clearJournal() {
    try {
      const res = await api.audit.clear();
      toast.success(`Audit journal cleared (${res?.deleted ?? 0} rows).`);
      setPage(0);
      try {
        const r = await api.audit.eventTypes();
        setEventTypes(r.eventTypes || []);
      } catch { /* noop */ }
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Failed to clear journal.');
    } finally {
      setConfirmClear(false);
    }
  }

  const total = data?.total ?? 0;
  const totalPages = data ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : 1;
  const hasFilters = Object.values(filters).some((v) => v && v.length > 0);

  return (
    <PageShell
      icon="fa-shield-halved"
      title="Audit"
      subtitle="Governance event journal — login, user changes, policy updates"
    >
      <div className="card overflow-hidden">
        {/* Filter grid — operator-set criteria narrowing the journal. */}
        <FilterGrid
          filters={filters}
          eventTypes={eventTypes}
          onChange={applyFilter}
        />

        {/* Actions row — Reset on the left, Refresh + Clear on the right.
            Mirrors the Logs panel header so the operator's muscle memory
            transfers (the kit lives in the same spot on both pages). */}
        <div className="flex items-center justify-between gap-3 border-b border-line bg-canvas/40 px-5 py-3">
          <button
            type="button"
            onClick={resetFilters}
            disabled={!hasFilters}
            className="btn-secondary text-xs"
          >
            <i className="fa-solid fa-filter-circle-xmark" aria-hidden="true" /> Reset filters
          </button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={load} disabled={loading} className="btn-secondary text-xs">
              <i className={`fa-solid fa-arrows-rotate ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => setConfirmClear(true)}
              className="btn-secondary text-xs text-rose-700 hover:bg-rose-soft"
            >
              <i className="fa-solid fa-trash" aria-hidden="true" />
              Clear
            </button>
          </div>
        </div>

        {error && (
          <div role="alert" className="border-b border-rose-200 bg-rose-soft px-5 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}

        <PaneStats data={data} loading={loading} page={page} totalPages={totalPages} />

        {!data ? null : data.items.length === 0 ? (
          <EmptyState
            icon="fa-clipboard-list"
            title="No matching events"
            description={
              hasFilters
                ? 'Try clearing or relaxing the filters.'
                : 'No governance events have been recorded yet.'
            }
            action={
              hasFilters && (
                <button type="button" onClick={resetFilters} className="btn-secondary">
                  <i className="fa-solid fa-filter-circle-xmark" aria-hidden="true" /> Reset filters
                </button>
              )
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-line bg-canvas/60 text-left text-[11px] font-semibold uppercase tracking-wider text-mist">
                    <th className="w-44 px-5 py-2.5">Time</th>
                    <th className="px-5 py-2.5">Event</th>
                    <th className="w-44 px-5 py-2.5">Actor</th>
                    <th className="w-32 px-5 py-2.5">Outcome</th>
                    <th className="w-36 px-5 py-2.5">IP</th>
                    <th className="w-10 px-5 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((evt) => (
                    <AuditRow key={evt.id} evt={evt} viewerTimezone={currentUser?.timezone} />
                  ))}
                </tbody>
              </table>
            </div>

            {total > PAGE_SIZE && (
              <Pager page={page} totalPages={totalPages} onChange={setPage} />
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={clearJournal}
        title="Clear audit journal?"
        message="This permanently deletes EVERY row in the audit_events table. The wipe itself will be recorded as the very next audit event (audit.clear). Continue?"
        confirmLabel="Clear audit journal"
        destructive
      />
    </PageShell>
  );
}

// -----------------------------------------------------------------------------
//  Filter grid — sits at the top of the panel, inside the same card as the
//  table. The Reset button moves to the actions row below; that's where it
//  also lives on the Logs page (right side, next to Refresh / Clear).
// -----------------------------------------------------------------------------
function FilterGrid({ filters, eventTypes, onChange }) {
  const [q, setQ] = useState(filters.q);
  // Debounce search.
  useEffect(() => {
    const id = setTimeout(() => {
      if (q !== filters.q) onChange({ q });
    }, 300);
    return () => clearTimeout(id);
  }, [q, filters.q, onChange]);

  // Keep local input synced if filters get reset externally.
  useEffect(() => { setQ(filters.q); }, [filters.q]);

  return (
    <div className="grid grid-cols-1 gap-3 border-b border-line px-5 py-4 sm:grid-cols-2 lg:grid-cols-6">
      <div className="lg:col-span-2">
        <label className="field-label">Search</label>
        <div className="relative">
          <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-mist" aria-hidden="true" />
          <input
            type="search"
            className="field pl-10"
            placeholder="Event, actor, IP, target, details…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className="field-label">Event type</label>
        <select className="field" value={filters.eventType} onChange={(e) => onChange({ eventType: e.target.value })}>
          <option value="">All events</option>
          {eventTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="field-label">Outcome</label>
        <select className="field" value={filters.outcome} onChange={(e) => onChange({ outcome: e.target.value })}>
          <option value="">Any</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
          <option value="denied">Denied</option>
        </select>
      </div>

      <div>
        <label className="field-label">Actor</label>
        <input
          className="field"
          placeholder="login fragment"
          value={filters.actor}
          onChange={(e) => onChange({ actor: e.target.value })}
        />
      </div>

      <div>
        <label className="field-label">Since (UTC)</label>
        <input
          type="datetime-local"
          className="field"
          value={filters.since}
          onChange={(e) => onChange({ since: e.target.value })}
        />
      </div>

      <div>
        <label className="field-label">Until (UTC)</label>
        <input
          type="datetime-local"
          className="field"
          value={filters.until}
          onChange={(e) => onChange({ until: e.target.value })}
        />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
//  Pane stats — shape mirrors logs-page.jsx PaneStats.
// -----------------------------------------------------------------------------
function PaneStats({ data, loading, page, totalPages }) {
  if (loading && !data) {
    return (
      <div className="px-5 py-12 text-center text-mist">
        <span className="loader-spinner mx-auto mb-2 text-accent" aria-hidden="true" />
        <div className="text-sm">Loading audit events…</div>
      </div>
    );
  }
  if (!data) return null;
  const total = data.total ?? 0;
  const start = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const end = Math.min((page + 1) * PAGE_SIZE, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-canvas/40 px-5 py-2 text-[11px] text-mist">
      <span>
        Showing <strong className="text-ink">{start}</strong>–<strong className="text-ink">{end}</strong>{' '}
        of <strong className="text-ink">{total}</strong> events
      </span>
      <span>Page {page + 1} of {totalPages}</span>
    </div>
  );
}

// -----------------------------------------------------------------------------
//  Row + details
// -----------------------------------------------------------------------------
function AuditRow({ evt, viewerTimezone }) {
  const [open, setOpen] = useState(false);
  const hasDetails = evt.details != null;
  const targetLabel = evt.targetKind && evt.targetId ? `${evt.targetKind}:${evt.targetId}` : null;

  return (
    <>
      <tr
        className="cursor-pointer border-b border-line last:border-b-0 hover:bg-canvas/40"
        onClick={() => setOpen((v) => !v)}
      >
        <td className="whitespace-nowrap px-5 py-2.5 align-top text-xs text-mist">
          {formatUtcInTimezone(evt.ts, viewerTimezone)}
        </td>
        <td className="px-5 py-2.5 align-top">
          <div className="flex items-center gap-2">
            <i className={`fa-solid ${eventIcon(evt.eventType)} w-4 text-mist`} aria-hidden="true" />
            <span className="font-mono text-[12px] text-ink">{evt.eventType}</span>
            {targetLabel && (
              <span className="hidden font-mono text-[11px] text-mist sm:inline">→ {targetLabel}</span>
            )}
          </div>
        </td>
        <td className="px-5 py-2.5 align-top">
          <span className="text-ink">{evt.actorLogin || <em className="text-mist">system</em>}</span>
        </td>
        <td className="px-5 py-2.5 align-top">
          <OutcomeBadge outcome={evt.outcome} />
        </td>
        <td className="px-5 py-2.5 align-top font-mono text-xs text-mist">
          {evt.ip || '—'}
        </td>
        <td className="px-5 py-2.5 align-top text-right">
          {hasDetails && (
            <i
              className={`fa-solid fa-chevron-${open ? 'up' : 'down'} text-[10px] text-mist`}
              aria-hidden="true"
            />
          )}
        </td>
      </tr>
      {open && (
        <tr className="bg-canvas/40">
          <td colSpan={6} className="px-5 py-3">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              <Detail label="User agent" value={evt.userAgent || '—'} mono />
              <Detail label="Target" value={targetLabel || '—'} mono />
              <Detail label="Audit ID" value={`#${evt.id}`} mono />
            </div>
            {hasDetails && (
              <div className="mt-3">
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-mist">Details</div>
                <pre className="overflow-x-auto rounded-xl bg-ink/95 p-3 text-[11px] text-white/90 scrollbar-thin">
                  {typeof evt.details === 'string'
                    ? evt.details
                    : JSON.stringify(evt.details, null, 2)}
                </pre>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-mist">{label}</div>
      <div className={`mt-0.5 break-words text-xs text-ink ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
//  Pagination — same component as logs-page.jsx Pager.
// -----------------------------------------------------------------------------
function Pager({ page, totalPages, onChange }) {
  const prev = () => onChange(Math.max(0, page - 1));
  const next = () => onChange(Math.min(totalPages - 1, page + 1));
  return (
    <div className="flex items-center justify-end gap-2 border-t border-line bg-canvas/40 px-5 py-3">
      <button type="button" onClick={() => onChange(0)} disabled={page === 0} className="btn-secondary text-xs">
        <i className="fa-solid fa-angles-left" aria-hidden="true" /> First
      </button>
      <button type="button" onClick={prev} disabled={page === 0} className="btn-secondary text-xs">
        <i className="fa-solid fa-angle-left" aria-hidden="true" /> Prev
      </button>
      <span className="px-2 text-xs text-mist">
        Page <strong className="text-ink">{page + 1}</strong> / {totalPages}
      </span>
      <button type="button" onClick={next} disabled={page >= totalPages - 1} className="btn-secondary text-xs">
        Next <i className="fa-solid fa-angle-right" aria-hidden="true" />
      </button>
      <button type="button" onClick={() => onChange(totalPages - 1)} disabled={page >= totalPages - 1} className="btn-secondary text-xs">
        Last <i className="fa-solid fa-angles-right" aria-hidden="true" />
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
//  Helpers
// -----------------------------------------------------------------------------
/**
 * `<input type="datetime-local">` returns `YYYY-MM-DDTHH:mm` without timezone.
 * Operators are used to UTC at this domain, and the field label says "UTC", so
 * we treat the value as UTC and append "Z".
 */
function toIsoUtc(local) {
  if (!local) return '';
  return /:\d{2}$/.test(local) && local.length >= 19
    ? `${local}Z`
    : `${local}:00Z`;
}
